import get from 'lodash/get'
import chunk from 'lodash/chunk'
import moment from 'moment'
import { shouldHideExpertInfo } from '@atheneum/api/dist/lib'

export function getAttendeeEmails(event) {
  if (!event) return []

  const emails = event.attendees.map(attendee => attendee.emailAddress.address)

  const organizerEmail = event.organizer.emailAddress.address
  return emails.filter(email => email !== organizerEmail)
}

export function generateScheduleTime({
  scheduleStartTime,
  scheduleEndTime,
  offset,
  timezone,
  country
}) {
  return `<p class="schedule-time">
      I am pleased to confirm the consultation call on <b>${scheduleStartTime.format(
    'dddd, MMMM DD, YYYY'
  )}</b> from <b>${scheduleStartTime.format(
    'HH:mm'
  )}</b> to <b>${scheduleEndTime.format(
    'HH:mm'
  )} (UTC${offset}) ${timezone} (${country}).</b>
    </p>
    `
}

export function generateExpertInviteMessage({
  staticData,
  zoomNumber,
  employeeSubmitted,
  expert,
  schedule,
  parentAccount,
  password
}) {
  const employeePositionId =
    employeeSubmitted.currentPosition.employeePositionId
  const userPosition =
    staticData.names.employeePositions[employeePositionId] || ''

  const userPhones = get(employeeSubmitted, 'address.phones', [])
  const userPhoneNumbers = userPhones.map(phone => phone.phoneNum)
  const { HEDGE_FUND } = staticData.CONST.ACCOUNT_TYPES

  const country = staticData.names.countries[expert.address.countryId]
  const timezone = staticData.names.timezones[expert.address.timezoneId]
  const scheduleStartTime = moment(schedule.startTime)
  scheduleStartTime.tz(timezone)
  const scheduleEndTime = moment(schedule.endTime)
  scheduleEndTime.tz(timezone)
  const offset = scheduleStartTime.tz(timezone).format('Z')

  const message = `
    <p>
      Dear ${expert.title} ${expert.fullName},
    </p>

    ${generateScheduleTime({
      scheduleStartTime,
      scheduleEndTime,
      country,
      timezone,
      offset
    })}

    ${generateZoomInfo({ staticData, zoomNumber, schedule, password, expert })}

    <p>
      You should be connected to our partners within a few minutes, please do hold the line.
    </p>

    <p>
      In case of any queries, please do not hesitate to contact me
      via email or via phone.
    </p>

    <p>
      Please accept this invitation before the time of consultation.
    </p>

    <p>
      ${employeeSubmitted.fullName}<br>
      ${userPosition}<br>
      ${employeeSubmitted.email}<br>
      ${userPhoneNumbers.join('<br>')}<br>
      <a href="www.atheneum-partners.com">www.atheneum-partners.com</a>
    </p>

    ${parentAccount.disclaimerComment || ''}

    ${
      parentAccount.accountTypeId === HEDGE_FUND &&
      parentAccount.hedgeFundRequired
        ? hfDisclaimer
        : ''
    }
  `

  return message
}

export function generateZoomInfo({ staticData, zoomNumber, schedule, password, clients, expert }) {
  if (!schedule) return ''

  const countryName = staticData.names.countries[zoomNumber.countryId]
  const zoomInfo = `
    <p>
      One-tap dial in for iPhones: <br/>
      ${renderPhoneNumbers(
        staticData,
        zoomNumber,
        schedule.zoomMeetingId,
        password
      )}
    <p/>

    <p>
      Local number(s) to dial in via regular phone: <br/>
      ${countryName} ${
    !!zoomNumber.internationalNumbers ? 'Toll' : 'Toll free'
  } ${zoomNumber.internationalNumbers.replace(',', ' or ')}
    </p>

    <p>
      Enter the following Meeting ID <b>${formatZoomMeetingId(
        schedule.zoomMeetingId
      )}#</b> <br/></b>
    </p>
    ${renderJoinUrl({ clients, expert })}
    <p>
      You can check all the available dial in numbers here: <a href="https://atheneum-partners.zoom.us/u/ab6dpgC1N3"> https://atheneum-partners.zoom.us/u/ab6dpgC1N3</a>
    </p>
  `

  return zoomInfo
}

function renderJoinUrl({ clients, expert }) {
  return `<p>
    If you would like to anonymously connect via internet call (Zoom), follow the link:
    ${clients ? renderJounUrlClients(clients) : renderJoinUrlExpert(expert)}
  </p>`
}

function renderJounUrlClients(clients) {
  return `<ul>
      ${clients.map(
        client =>
          `<li>${client.actualName} <a href="${client.joinUrl}">${client.joinUrl}</a></li>`
      )}
    </ul>`
}

function renderJoinUrlExpert(expert) {
  return `<a href="${expert.joinUrl}">
      ${expert.joinUrl}
    </a> <br/>`
}

/**
 * Takes a zoom meeting ID like 752123456#, and
 * groups the numbers: 752-123-456
 *
 * @param {string|number} zoomMeetingId
 * @returns {string} formatted ID
 */
function formatZoomMeetingId(zoomMeetingId) {
  const zoomMeetingIdNumberArray = [...String(zoomMeetingId)]
  const zoomMeetingNumberGroups = chunk(zoomMeetingIdNumberArray, 3)
  return zoomMeetingNumberGroups.map(group => group.join('')).join('-')
}

export function generateClientInviteMessage({
  zoomNumber,
  user,
  address,
  clients,
  schedule,
  staticData,
  epl,
  parentAccount
}) {
  const employeePositionId = user.currentPosition.employeePositionId
  const userPosition = staticData.names.employeePositions[employeePositionId]
  const userPhones = address.phones || []
  const userPhoneNumbers = userPhones.map(phone => phone.phoneNum)
  const clientsMessage = clients
    .map(client => `${client.title} ${client.fullName}`)
    .join(', ')

  const expertPosition = get(
    epl,
    'relevantExperience.experience.position.name',
    ''
  )
  const expertCompany = get(
    epl,
    'relevantExperience.experience.company.name',
    ''
  )
  const expertLocation = get(epl, 'relevantExperience.experience.location', '')
  const relevantSegment = get(epl, 'segment.name', '')

  const isExpertNameShown =
    !epl.hideExpertName &&
    parentAccount.expertsDisclosed &&
    !shouldHideExpertInfo({ parentAccountId: parentAccount.id })

  let expertInfo
  if (isExpertNameShown) {
    expertInfo = `
      ${epl.expert.title} ${epl.expert.fullName ||
      'Expert'}, ${expertPosition}, ${expertCompany}, ${expertLocation}

      for Segment: ${relevantSegment}
    `
  } else {
    expertInfo = `
      expert${expertLocation ? ` from ${expertLocation}` : ''
      }, ${expertPosition}, ${expertCompany},

      for Segment: ${relevantSegment}
    `
  }

  const message = `
    <p>
      Dear ${clientsMessage},
    </p>

    <p>
      I am pleased to confirm the consultation call scheduled with ${expertInfo}
    </p>

    ${generateZoomInfo({ staticData, zoomNumber, schedule, password: epl.id, clients })}

    <p>
      You should be connected to our Expert within a few minutes, please do hold the line.
    </p>

    <p>
      In case of any queries, please do not hesitate to contact me
      via email or via phone.
    </p>

    <p>
      Please accept this invitation before the time of consultation.
    </p>

    <p>
      ${user.fullName}<br>
      ${userPosition}<br>
      ${user.email}<br>
      ${userPhoneNumbers.join('<br>')}<br>
      <a href="www.atheneum-partners.com">www.atheneum-partners.com</a>
    </p>
  `
  return message
}

export function getZoomNumber({ internationalNumbers, tollFreeNumbers }) {
  if (tollFreeNumbers) {
    const tollFreeNums = tollFreeNumbers.split(',')
    if (tollFreeNums.length) return tollFreeNums[0]
  }

  if (internationalNumbers) {
    const internationalNums = internationalNumbers.split(',')
    if (internationalNums.length) return internationalNums[0]
  }
}

function renderPhoneNumbers(
  staticData,
  zoomNumber,
  zoomMeetingId,
  zoomMeetingPassword
) {
  const noNumbersFoundMessage =
    'No local numbers found, please find a number on the link below'
  if (!zoomNumber) {
    return noNumbersFoundMessage
  }

  const { internationalNumbers, tollFreeNumbers, countryId } = zoomNumber

  const countryName = staticData.names.countries[countryId]

  const internationalNums = internationalNumbers
    .split(',')
    .map(
      (num, i) => {
        const numNoSpaces = num.split(' ').join('')
        const formattedNum = numNoSpaces.split('(')[0]
        return `${countryName} test toll number ${i += 1}: <a href="tel:${formattedNum})},,${zoomMeetingId}#,,,,,,#,,,${zoomMeetingPassword}#">1-Tap</a> ${num}`
      })
    .join('<br/>')

  if (internationalNums) {
    return internationalNums
  }

  const tollFreeNums = tollFreeNumbers
    .split(',')
    .map((num, i) => `${countryName} Toll Free number ${i += 1}: ${num}`)
    .join('<br />')

  if (tollFreeNums) {
    return tollFreeNums
  }
}

export function generateLocationLabel(zoomNumber, schedule) {
  if (!zoomNumber) return 'Conference call'

  const zoomCallNumber = getZoomNumber(zoomNumber)
  if (!zoomCallNumber) {
    return `Conference call ${formatZoomMeetingId(schedule.zoomMeetingId)}#`
  }

  return `Conference call ${zoomCallNumber} // ${formatZoomMeetingId(
    schedule.zoomMeetingId
  )}#`
}

export function generateCallSubject(schedule) {
  return get(schedule, 'zoomMeeting.topic', 'Atheneum call')
}

const hfDisclaimer =
  '<p><i>Atheneum thanks you for your participation in today’s consultation. Before we begin, we would like to remind you that you are not to disclose any material non-public information, or any confidential information, concerning your company or any other company discussed today. If at any time, a question is asked that touches on an area where you do have confidential information, please decline to answer. If you are unsure as to whether or not you may be breaching any obligation to a third party by disclosing a piece of information, please err on the side of caution and kindly decline to answer the question.</i></p>'
